import { useCallback, useMemo, useState } from 'react';
import type { ChatSearchMatchMode } from '@fias/platform-resources';
import {
  CHAT_SEARCH_DEFAULT_MATCH_MODE,
  searchDayBounds,
  type ChatSearchFilters,
} from './useChatMessageSearch';
import {
  EMPTY_SEARCH_DAY_RANGE,
  absorbChatSearchOperators,
  isDayRangeInvalid,
  type AbsorbableOperator,
  type SearchDayRange,
  type SearchHasValue,
} from '../components/message-search-utils';

export interface ChatSearchControls {
  /** The search box's text, with any absorbed operators already removed. */
  rawQuery: string;
  /** Handles typing: absorbs completed operators into the controls below. */
  setRawQuery: (value: string) => void;
  senderFilter: string | null;
  setSenderFilter: (value: string | null) => void;
  matchMode: ChatSearchMatchMode;
  setMatchMode: (value: ChatSearchMatchMode) => void;
  contentFilter: SearchHasValue;
  setContentFilter: (value: SearchHasValue) => void;
  dayRange: SearchDayRange;
  setDayRange: (value: SearchDayRange) => void;
  /** An `in:` operator's conversation name, when the surface accepts one. */
  inConversation: string | null;
  setInConversation: (value: string | null) => void;
  /**
   * False while the controls cannot form a valid search — today, a backwards
   * day range. The panels gate their searches on it; `filters` always
   * reflects the controls as set, so there is one authority on "may this run".
   */
  ready: boolean;
  /** The controls as the filter set the search hooks take. */
  filters: ChatSearchFilters;
  /** Back to an empty box and default controls. */
  reset: () => void;
}

/**
 * The state behind a chat search panel's box and filter controls, shared by
 * the in-channel and all-chats panels so the two never drift. Typing an
 * operator (`from:bob `, `has:link `, `after:2030-01-01 `) moves it out of
 * the box and into the matching control the moment it is complete; the
 * controls stay editable on their own, and a later operator for the same
 * control simply replaces its value (last one wins, like re-picking from the
 * control). `allowedOperators` says which operators this surface absorbs —
 * `in:` only makes sense across conversations. `initialQuery` goes through
 * the same absorber, so a handed-off `has:link deploy` lands in the controls
 * rather than waiting for a keystroke.
 */
export function useChatSearchControls(
  allowedOperators: ReadonlySet<AbsorbableOperator>,
  initialQuery = '',
): ChatSearchControls {
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  const [matchMode, setMatchMode] = useState<ChatSearchMatchMode>(CHAT_SEARCH_DEFAULT_MATCH_MODE);
  const [contentFilter, setContentFilter] = useState<SearchHasValue>(null);
  const [dayRange, setDayRange] = useState<SearchDayRange>(EMPTY_SEARCH_DAY_RANGE);
  const [inConversation, setInConversation] = useState<string | null>(null);

  const absorb = useCallback(
    (value: string): string => {
      const absorbed = absorbChatSearchOperators(value, allowedOperators);
      if (absorbed.from !== undefined) setSenderFilter(absorbed.from);
      if (absorbed.in !== undefined) setInConversation(absorbed.in);
      if (absorbed.has !== undefined) setContentFilter(absorbed.has);
      if (absorbed.after !== undefined || absorbed.before !== undefined) {
        setDayRange((prev) => ({
          after: absorbed.after ?? prev.after,
          before: absorbed.before ?? prev.before,
        }));
      }
      return absorbed.text;
    },
    [allowedOperators],
  );

  // Lazy init so the hand-off text is absorbed exactly once, on mount.
  const [rawQuery, setRawQueryState] = useState(() => absorb(initialQuery));

  const setRawQuery = useCallback((value: string) => setRawQueryState(absorb(value)), [absorb]);

  const reset = useCallback(() => {
    setRawQueryState('');
    setSenderFilter(null);
    setMatchMode(CHAT_SEARCH_DEFAULT_MATCH_MODE);
    setContentFilter(null);
    setDayRange(EMPTY_SEARCH_DAY_RANGE);
    setInConversation(null);
  }, []);

  const ready = !isDayRangeInvalid(dayRange);

  const filters = useMemo<ChatSearchFilters>(
    () => ({
      senderUsername: senderFilter,
      matchMode,
      has: contentFilter,
      ...searchDayBounds(dayRange.after, dayRange.before),
    }),
    [senderFilter, matchMode, contentFilter, dayRange],
  );

  return {
    rawQuery,
    setRawQuery,
    senderFilter,
    setSenderFilter,
    matchMode,
    setMatchMode,
    contentFilter,
    setContentFilter,
    dayRange,
    setDayRange,
    inConversation,
    setInConversation,
    ready,
    filters,
    reset,
  };
}
